import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import {
  BulkInvitationColumnSpec,
  BulkInvitationFieldSpec,
  BulkInvitationRowError,
  BulkInvitationRowValues,
  validateBulkInvitationRow,
} from "legal-entities/invitations/bulk/bulk-invitation-column-spec";
import { useState } from "react";

export type BulkInvitationRowEditorProps = {
  readonly spec: BulkInvitationColumnSpec;
  readonly open: boolean;
  readonly title: string;
  readonly row: BulkInvitationRowValues;
  /**
   * The rows above this one, so the editor can tell the person they have already entered this
   * address before they close the dialog on a row that was never going to be sent.
   */
  readonly precedingRows: readonly BulkInvitationRowValues[];
  readonly onCancel: () => void;
  readonly onSave: (row: BulkInvitationRowValues) => void;
};

/**
 * One row, edited as a form rather than as cells.
 *
 * A row here has five or more fields that mean something only together - a name, an address the
 * firm has to accept, a title the client reads, an address that must not be blank in every part.
 * Editing them cell by cell asks the person to hold that whole rule in their head while tabbing
 * sideways; a form validates the row when they are finished with it and says what is wrong in one
 * place. It is also the shape `permissions-matrix-table` settled on, with `editDisplayMode:
 * "custom"`, and per-cell editors in a dense table are what trips the React Compiler.
 */
export default function BulkInvitationRowEditor(props: Readonly<BulkInvitationRowEditorProps>) {
  const [draft, setDraft] = useState<BulkInvitationRowValues>(props.row);
  const [errors, setErrors] = useState<readonly BulkInvitationRowError[]>([]);
  // The dialog is mounted per row it opens on, keyed by the caller, so the draft does not need
  // resetting on close - the next open gets a new component with a new initial row.

  function messageFor(key: string): string | undefined {
    return errors.find((error) => error.key === key)?.message;
  }

  function setValue(key: string, value: string | boolean) {
    setDraft({ ...draft, [key]: value });
    // Clearing this field's message as they type keeps a corrected field from still looking wrong
    // while they work down the form. The rest stand until they try to save again.
    setErrors(errors.filter((error) => error.key !== key));
  }

  function save() {
    const found = validateBulkInvitationRow(props.spec, draft, props.precedingRows);
    setErrors(found);
    if (found.length === 0) props.onSave(draft);
  }

  function renderField(field: BulkInvitationFieldSpec) {
    const message = messageFor(field.key);

    if (field.kind === "boolean") {
      return (
        <div key={field.key}>
          <FormControlLabel
            control={
              <Checkbox
                checked={draft[field.key] === true}
                onChange={(event) => setValue(field.key, event.target.checked)}
                slotProps={{ input: { "aria-label": field.header } }}
              />
            }
            label={field.header}
          />
          {field.hint ? <FormHelperText>{field.hint}</FormHelperText> : null}
        </div>
      );
    }

    const value = typeof draft[field.key] === "string" ? (draft[field.key] as string) : "";

    if (field.kind === "select") {
      return (
        <TextField
          key={field.key}
          select
          fullWidth
          label={field.header}
          value={value}
          error={Boolean(message)}
          helperText={message ?? field.hint}
          onChange={(event) => setValue(field.key, event.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        >
          <MenuItem value="">None</MenuItem>
          {(field.options ?? []).map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    return (
      <TextField
        key={field.key}
        fullWidth
        required={field.required}
        label={field.header}
        value={value}
        type={field.kind === "email" ? "email" : "text"}
        error={Boolean(message)}
        helperText={message ?? field.hint}
        onChange={(event) => setValue(field.key, event.target.value)}
      />
    );
  }

  return (
    <Dialog open={props.open} onClose={props.onCancel} fullWidth maxWidth="sm">
      <DialogTitle>{props.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {props.spec.fields.map(renderField)}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onCancel}>Cancel</Button>
        <Button variant="contained" onClick={save}>
          Save row
        </Button>
      </DialogActions>
    </Dialog>
  );
}
